import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project-imports
import ErrorBoundary from './ErrorBoundary';
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import RoleGuard from 'utils/route-guard/RoleGuard';

const DashboardPage = Loadable(lazy(() => import('pages/sentinel/dashboard')));
const LiveReportPage = Loadable(lazy(() => import('pages/sentinel/live-report')));
const SystemDebugPage = Loadable(lazy(() => import('pages/sentinel/system-debug')));
const CommandRejectionsPage = Loadable(lazy(() => import('pages/sentinel/command-rejections')));
const DevicesPage = Loadable(lazy(() => import('pages/sentinel/devices')));
const BatteryHealthPage = Loadable(lazy(() => import('pages/sentinel/battery-health')));
const DeviceDetailPage = Loadable(lazy(() => import('pages/sentinel/device-detail')));
const MapPage = Loadable(lazy(() => import('pages/sentinel/map')));
const CommandsPage = Loadable(lazy(() => import('pages/sentinel/commands')));
const CommandDetailPage = Loadable(lazy(() => import('pages/sentinel/command-detail')));
const RequestTypesPage = Loadable(lazy(() => import('pages/sentinel/request-types')));
const DeviceModelsPage = Loadable(lazy(() => import('pages/sentinel/device-models')));
const KnowledgePage = Loadable(lazy(() => import('pages/sentinel/knowledge')));
const KnowledgeDetailPage = Loadable(lazy(() => import('pages/sentinel/knowledge-detail')));
const KnowledgeCategoriesPage = Loadable(lazy(() => import('pages/sentinel/knowledge-categories')));
const KnowledgeEditorPage = Loadable(lazy(() => import('pages/sentinel/knowledge-editor')));
const PushPage = Loadable(lazy(() => import('pages/sentinel/push')));
const OtaPage = Loadable(lazy(() => import('pages/sentinel/ota')));
const CrashesPage = Loadable(lazy(() => import('pages/sentinel/crashes')));
const TenantsPage = Loadable(lazy(() => import('pages/sentinel/customers')));
const AnnouncementsPage = Loadable(lazy(() => import('pages/sentinel/announcements')));
const UpdateApprovalsPage = Loadable(lazy(() => import('pages/sentinel/update-approvals')));
const UpdateRolloutsPage = Loadable(lazy(() => import('pages/sentinel/update-rollouts')));
const UsersPage = Loadable(lazy(() => import('pages/sentinel/users')));
const ProfilePage = Loadable(lazy(() => import('pages/sentinel/profile')));
const SettingsPage = Loadable(lazy(() => import('pages/sentinel/settings')));

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      element: <DashboardLayout />,
      errorElement: <ErrorBoundary />,
      children: [
        {
          index: true,
          element: <Navigate to="/dashboard" replace />
        },
        {
          path: 'dashboard',
          element: <DashboardPage />
        },
        {
          path: 'live-report',
          element: (
            <RoleGuard allowedRoles={['super_admin']}>
              <LiveReportPage />
            </RoleGuard>
          )
        },
        {
          path: 'system-debug',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support']}>
              <SystemDebugPage />
            </RoleGuard>
          )
        },
        {
          path: 'command-rejections',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support']}>
              <CommandRejectionsPage />
            </RoleGuard>
          )
        },
        {
          path: 'devices',
          element: <DevicesPage />
        },
        {
          path: 'battery-health',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin', 'tenant_tech']}>
              <BatteryHealthPage />
            </RoleGuard>
          )
        },
        {
          path: 'devices/:serial',
          element: <DeviceDetailPage />
        },
        {
          path: 'map',
          element: <MapPage />
        },
        {
          path: 'commands',
          element: (
            <RoleGuard allowedRoles={['super_admin']}>
              <CommandsPage />
            </RoleGuard>
          )
        },
        {
          path: 'commands/:taskid',
          element: (
            <RoleGuard allowedRoles={['super_admin']}>
              <CommandDetailPage />
            </RoleGuard>
          )
        },
        {
          path: 'request-types',
          element: (
            <RoleGuard allowedRoles={['super_admin']}>
              <RequestTypesPage />
            </RoleGuard>
          )
        },
        {
          path: 'device-models',
          element: (
            <RoleGuard allowedRoles={['super_admin']}>
              <DeviceModelsPage />
            </RoleGuard>
          )
        },
        {
          path: 'knowledge',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin', 'tenant_tech']}>
              <KnowledgePage />
            </RoleGuard>
          )
        },
        {
          path: 'knowledge/new',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin', 'tenant_tech']}>
              <KnowledgeEditorPage />
            </RoleGuard>
          )
        },
        {
          path: 'knowledge/:id',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin', 'tenant_tech']}>
              <KnowledgeDetailPage />
            </RoleGuard>
          )
        },
        {
          path: 'knowledge/:id/edit',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin', 'tenant_tech']}>
              <KnowledgeEditorPage />
            </RoleGuard>
          )
        },
        {
          path: 'knowledge/categories',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin']}>
              <KnowledgeCategoriesPage />
            </RoleGuard>
          )
        },
        {
          path: 'push',
          element: <PushPage />
        },
        {
          path: 'ota',
          element: <OtaPage />
        },
        {
          path: 'crashes',
          element: (
            <RoleGuard allowedRoles={['super_admin']}>
              <CrashesPage />
            </RoleGuard>
          )
        },
        {
          path: 'customers',
          element: (
            <RoleGuard allowedRoles={['super_admin']}>
              <TenantsPage />
            </RoleGuard>
          )
        },
        {
          path: 'announcements',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin', 'tenant_tech']}>
              <AnnouncementsPage />
            </RoleGuard>
          )
        },
        {
          path: 'update-approvals',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin']}>
              <UpdateApprovalsPage />
            </RoleGuard>
          )
        },
        {
          path: 'update-rollouts',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin', 'tenant_tech']}>
              <UpdateRolloutsPage />
            </RoleGuard>
          )
        },
        {
          path: 'users',
          element: (
            <RoleGuard allowedRoles={['super_admin', 'support', 'tenant_super_admin', 'tenant_admin']}>
              <UsersPage />
            </RoleGuard>
          )
        },
        {
          path: 'profile',
          element: <ProfilePage />
        },
        {
          path: 'settings',
          element: <SettingsPage />
        }
      ]
    }
  ]
};

export default MainRoutes;
